// Return an object with the main attributes that apply for different types of filters (single, range, group)

import { Aggregation, Filter, FilterTypes, QueryCriteria } from '../filters';

// ---------------------------------------------------------------------------
// persistentSelection utilities
// ---------------------------------------------------------------------------

export type PersistentEntry = { key: string | number; displayName: string };

/**
 * Write the persistentSelection back onto a Filter from a populated cache.
 * For single-value filters (equal): pass a single parsed value.
 * For multi-value filters (multi-select): pass an array of parsed values.
 */
export const writePersistentSelection = (
  filter: Filter,
  selectedValues: any | any[],
  cache: Map<string | number, Aggregation>
): void => {
  const values: any[] = Array.isArray(selectedValues) ? selectedValues : [selectedValues];

  if (values.length === 0 || values[0] == null) {
    filter.persistentSelection = [];
    return;
  }

  filter.persistentSelection = values.map(v => {
    const cached = cache.get(v);
    const displayName = cached?.displayName || null;
    return cached
      ? { key: cached.key, displayName: displayName ?? String(cached.key) }
      : { key: v, displayName: String(v) };
  });
};

/**
 * Populate a cache Map from fresh aggregation data and, for any selected values
 * still missing, fall back to existing persistentSelection entries.
 *
 * @param cache            The mutable Map to update (pass a copy or the existing one).
 * @param selectedValues   Currently selected value(s) — scalar or array.
 * @param aggregations     Fresh aggregation list from the API.
 * @param filter           The Filter object (reads persistentSelection as fallback).
 * @param valueParser      Converts an Aggregation → the parsed value used as FormControl value.
 */
export const updateSelectionCache = (
  cache: Map<string | number, Aggregation>,
  selectedValues: any | any[],
  aggregations: Aggregation[],
  filter: Filter,
  valueParser: (agg: Aggregation) => string | number = agg => agg.key
): void => {
  const values: any[] = Array.isArray(selectedValues) ? selectedValues : selectedValues != null ? [selectedValues] : [];

  if (values.length === 0) return;

  // Priority 1: fresh API data
  aggregations.forEach(agg => {
    const parsed = valueParser(agg);
    if (values.includes(parsed)) {
      cache.set(parsed, agg);
    }
  });

  // Priority 2: persistentSelection fallback for still-missing values
  if (filter.persistentSelection?.length) {
    const aggKeys = new Set(aggregations.map(o => valueParser(o)));
    filter.persistentSelection.forEach(({ key, displayName }) => {
      const syntheticAgg: Aggregation = { key: String(key), value: 0, displayName };
      const parsed = valueParser(syntheticAgg);
      if (values.includes(parsed) && !aggKeys.has(parsed)) {
        cache.set(parsed, syntheticAgg);
      }
    });
  }
};

type MapFilterOption = Omit<Partial<QueryCriteria>, 'key'> & Partial<Filter>;

// to avoid code repetition when mapping filters and filter groups
const getMainAttributes = (filter: MapFilterOption) => {
  const { field, key, dataType, condition } = filter;
  const groupKey = filter.group_key ? { group_key: filter.group_key } : {};

  return { field, key, dataType, condition, ...groupKey };
};

export const mapFilter = (filter: MapFilterOption): MapFilterOption => {
  const { values, type, associations } = filter;

  if (associations != null) {
    return mapFilterGroup(filter);
  }

  const valuesObj = !!values ? { values } : {};
  // const includeExcludeConfigs = getFilterAdditionalConfig(filter) ?? {};
  const mainAttributes = getMainAttributes(filter);
  return { ...valuesObj, type, ...mainAttributes };
};

export const mapFilterGroup = (filter: MapFilterOption): MapFilterOption => {
  const { type: filterType, associations } = filter;
  const type = FilterTypes.Group;

  const mainAttributes = getMainAttributes(filter);

  const values = [
    ...associations.map((association: Filter) => mapFilter(association)),
    { type: filterType, ...mainAttributes },
  ];

  return { values, type, ...mainAttributes };
};

export const convertFilterWithAssociations = (filter: MapFilterOption): MapFilterOption => {
  if (!filter?.associations?.length) return filter;

  // parent without its associations
  const { associations: childAssociations, ...parent } = filter as any;

  // normalize children recursively (preserve group's group_key if child missing it)
  const children = (childAssociations as MapFilterOption[]).map(child =>
    convertFilterWithAssociations({ ...child, group_key: child.group_key ?? filter.group_key } as MapFilterOption)
  );

  // parent as an association element (no nested associations)
  const parentAsAssociation = (({ _associations, ...p }) => p)(parent) as MapFilterOption;
  if (!parentAsAssociation.group_key && filter.group_key) parentAsAssociation.group_key = filter.group_key;

  const associations = [parentAsAssociation, ...children];

  return {
    type: 'group',
    associations,
    group_key: filter.group_key,
    closeIcon: filter.closeIcon,
    disabled: filter.disabled,
    selectItemsInGroup: filter.selectItemsInGroup,
    ...getMainAttributes(filter),
  } as MapFilterOption;
};

export const normalizeFilters = (filters: Filter[] = []): MapFilterOption[] =>
  filters.map(f => convertFilterWithAssociations(f));
